// App.jsx
// Root component. Configures React Router v6 with all page routes.
// Wraps the app in AuthProvider and MemoryProvider so every page has
// access to auth state and user memory without prop drilling.
//
// Route structure:
//   /             → redirects to /dashboard (auth) or /login (no auth)
//   /login        → Login page (public)
//   /signup       → Signup page (public)
//   /onboarding   → Onboarding page (protected)
//   /dashboard    → Dashboard page (protected)
//   /pillar/:pillarId → Pillar page (protected)

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, MemoryProvider, useAuth } from './components/MemoryContext';

import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Pillar from './pages/Pillar';

// ProtectedRoute wraps any route that requires the user to be logged in.
// Unauthenticated users are sent to /login; authenticated users see the page.
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? children : <Navigate to="/login" replace />;
}

// RootRedirect sends / to the right place based on auth state.
function RootRedirect() {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated() ? '/dashboard' : '/login'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pillar/:pillarId"
        element={
          <ProtectedRoute>
            <Pillar />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MemoryProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </MemoryProvider>
    </AuthProvider>
  );
}
